import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Typography, List, InputNumber, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { ViewService } from '../../Services/ViewService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { WebApiModelsView, WebApiViewDtoCreateViewDto, WebApiViewDtoUpdateViewDto, WebApiModelsMemberField, WebApiViewDtoCreateViewColumnDto, WebApiModelsEnumerationTypesMemberFieldTypes } from '../../Repository/eventbookyapi';
import { TextService } from '../../Services/TextService';
import LoadingSpinner from '../../Components/Shared/LoadingSpinner';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftSettings from '../Account/LeftSettings';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';

const { Title } = Typography;
const { Option } = Select;

interface ViewColumn {
    sortOrder: number;
    columnType?: WebApiModelsEnumerationTypesMemberFieldTypes;
    customFieldId?: number | null;
    fieldName?: string;
}

const Views: React.FC = () => {
    const [views, setViews] = useState<WebApiModelsView[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingView, setEditingView] = useState<WebApiModelsView | null>(null);
    const [memberFields, setMemberFields] = useState<WebApiModelsMemberField[]>([]);
    const [viewColumns, setViewColumns] = useState<ViewColumn[]>([]);
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    const ViewTypeOptions = [
        { value: 0, label: GetText('Views_ViewTypeMember') }
    ];

    // Load views and member fields on component mount
    useEffect(() => {
        loadViews();
        loadMemberFields();
    }, []);

    const loadViews = async () => {
        try {
            setLoading(true);
            const response = await ViewService.GetAllViews();
            setViews(response);
        } catch (error) {
            console.error('Error loading views:', error);
            message.error(GetText('Views_FailedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    const loadMemberFields = async () => {
        try {
            const fields = await MemberFieldService.GetMemberFields();
            setMemberFields(fields);
        } catch (error) {
            console.error('Error loading member fields:', error);
            message.error(GetText('Views_FailedToLoadFields'));
        }
    };

    const handleCreate = () => {
        setEditingView(null);
        setViewColumns([]);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (view: WebApiModelsView) => {
        setEditingView(view);
        form.setFieldsValue({
            name: view.name,
            viewType: view.viewType,
            customListId: view.customListId
        });

        // Load existing view columns
        const columns: ViewColumn[] = (view.viewColumns || []).map(col => {
            const fieldName = mapDynamicInputLabel(col.columnType as any, memberFields, col.customFieldId);
            return {
                sortOrder: col.sortOrder || 0,
                columnType: col.columnType,
                customFieldId: col.customFieldId,
                fieldName: fieldName || undefined
            };
        }).sort((a, b) => a.sortOrder - b.sortOrder);

        setViewColumns(columns);
        setModalVisible(true);
    };

    const handleDelete = async (viewId: number) => {
        try {
            await ViewService.DeleteView(viewId);
            message.success(GetText('Views_DeletedSuccessfully'));
            loadViews();
        } catch (error) {
            console.error('Error deleting view:', error);
            message.error(GetText('Views_FailedToDelete'));
        }
    };

    const handleAddColumn = (fieldId: number) => {
        const field = memberFields.find(f => f.id === fieldId);
        if (!field) return;

        const fieldName = mapDynamicInputLabel(field.columnType as any, memberFields, field.id);

        const newColumn: ViewColumn = {
            sortOrder: viewColumns.length,
            columnType: field.columnType,
            customFieldId: field.columnType === MemberFieldTypes.Custom ? field.id : null,
            fieldName: fieldName || undefined
        };

        setViewColumns([...viewColumns, newColumn]);
    };

    const handleRemoveColumn = (index: number) => {
        const newColumns = viewColumns.filter((_, i) => i !== index);
        // Re-index sortOrder
        newColumns.forEach((col, i) => col.sortOrder = i);
        setViewColumns(newColumns);
    };

    const handleMoveColumn = (index: number, direction: 'up' | 'down') => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === viewColumns.length - 1)) {
            return;
        }

        const newColumns = [...viewColumns];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        [newColumns[index], newColumns[targetIndex]] = [newColumns[targetIndex], newColumns[index]];

        // Update sortOrder
        newColumns.forEach((col, i) => col.sortOrder = i);
        setViewColumns(newColumns);
    };

    const handleSubmit = async (values: any) => {
        try {
            const viewColumnsDto: WebApiViewDtoCreateViewColumnDto[] = viewColumns.map(col => ({
                sortOrder: col.sortOrder,
                columnType: col.columnType,
                customFieldId: col.customFieldId
            }));

            if (editingView) {
                // Update existing view
                const updateData: WebApiViewDtoUpdateViewDto = {
                    name: values.name,
                    viewType: values.viewType,
                    customListId: values.customListId || null,
                    viewColumns: viewColumnsDto
                };
                await ViewService.UpdateView(editingView.id!, updateData);
                message.success(GetText('Views_UpdatedSuccessfully'));
            } else {
                // Create new view
                const createData: WebApiViewDtoCreateViewDto = {
                    name: values.name,
                    viewType: values.viewType,
                    customListId: values.customListId || null,
                    viewColumns: viewColumnsDto
                };
                await ViewService.CreateView(createData);
                message.success(GetText('Views_CreatedSuccessfully'));
            }

            setModalVisible(false);
            form.resetFields();
            setViewColumns([]);
            loadViews();
        } catch (error) {
            console.error('Error saving view:', error);
            message.error(GetText('Views_FailedToSave'));
        }
    };

    const columns = [
        {
            title: GetText('Views_ColumnID'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: GetText('Views_ColumnName'),
            dataIndex: 'name',
            key: 'name',
            width: 250,
            sorter: (a: WebApiModelsView, b: WebApiModelsView) =>
                (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: GetText('Views_ColumnViewType'),
            dataIndex: 'viewType',
            key: 'viewType',
            width: 150,
            render: (viewType: number) => {
                const option = ViewTypeOptions.find(opt => opt.value === viewType);
                return option?.label || `Type ${viewType}`;
            },
            filters: ViewTypeOptions.map(option => ({
                text: option.label,
                value: option.value
            })),
            onFilter: (value: boolean | React.Key, record: WebApiModelsView) =>
                record.viewType === value,
        },
        {
            title: GetText('Views_ColumnCreatedAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: Date) => date ? new Date(date).toLocaleString() : '-',
            sorter: (a: WebApiModelsView, b: WebApiModelsView) =>
                (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0),
        },
        {
            title: GetText('Views_ColumnUpdatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            render: (date: Date | null) => date ? new Date(date).toLocaleString() : '-',
        },
        {
            title: GetText('Views_ColumnActions'),
            key: 'actions',
            width: 150,
            fixed: 'right' as const,
            render: (_: any, record: WebApiModelsView) => (
                <Space size="middle">
                    <EditOutlined
                        style={{ fontSize: "20px", cursor: "pointer", color: "#1890ff" }}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={GetText('Views_DeleteConfirmTitle')}
                        description={GetText('Views_DeleteConfirmDescription')}
                        onConfirm={() => handleDelete(record.id!)}
                        okText={GetText('Yes')}
                        cancelText={GetText('No')}
                    >
                        <DeleteOutlined
                            style={{ color: "red", fontSize: "20px", cursor: "pointer" }}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <LayoutLeftMenu title={GetText("Views_Title")} menu={<LeftSettings selected={"views"} />}>
                <LoadingSpinner loadingText={GetText('Views_LoadingText')} />
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu title={GetText("Views_Title")} menu={<LeftSettings selected={"views"} />}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    {GetText('Views_AddNewView')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={views}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title={editingView ? GetText('Views_ModalTitleEdit') : GetText('Views_ModalTitleCreate')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setViewColumns([]);
                }}
                onOk={() => form.submit()}
                okText={editingView ? GetText('Views_ButtonUpdate') : GetText('Views_ButtonCreate')}
                cancelText={GetText('Views_ButtonCancel')}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label={GetText('Views_LabelViewName')}
                        name="name"
                        rules={[
                            { required: true, message: GetText('Views_ValidationViewNameRequired') },
                            { min: 2, message: GetText('Views_ValidationViewNameMin') }
                        ]}
                    >
                        <Input placeholder={GetText('Views_PlaceholderViewName')} />
                    </Form.Item>

                    <Form.Item
                        label={GetText('Views_LabelViewType')}
                        name="viewType"
                        rules={[
                            { required: true, message: GetText('Views_ValidationViewTypeRequired') }
                        ]}
                        initialValue={0}
                    >
                        <Select
                            style={{ width: '100%' }}
                            placeholder={GetText('Views_PlaceholderViewType')}
                        >
                            {ViewTypeOptions.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Card title={GetText('Views_CardTitleViewColumns')} size="small" style={{ marginTop: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={GetText('Views_PlaceholderSelectField')}
                                onChange={handleAddColumn}
                                value={undefined}
                            >
                                {memberFields.map(field => (
                                    <Option key={field.id} value={field.id}>
                                        {mapDynamicInputLabel(field.columnType as any, memberFields, field.id)}
                                    </Option>
                                ))}
                            </Select>

                            {viewColumns.length > 0 ? (
                                <List
                                    size="small"
                                    bordered
                                    dataSource={viewColumns}
                                    renderItem={(column, index) => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="up"
                                                    size="small"
                                                    icon={<ArrowUpOutlined />}
                                                    onClick={() => handleMoveColumn(index, 'up')}
                                                    disabled={index === 0}
                                                />,
                                                <Button
                                                    key="down"
                                                    size="small"
                                                    icon={<ArrowDownOutlined />}
                                                    onClick={() => handleMoveColumn(index, 'down')}
                                                    disabled={index === viewColumns.length - 1}
                                                />,
                                                <Button
                                                    key="delete"
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleRemoveColumn(index)}
                                                />
                                            ]}
                                        >
                                            <Space>
                                                <span style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                                                <span>{column.fieldName}</span>
                                                <span style={{ color: '#999' }}>({GetText('Views_ColumnOrderLabel')}: {column.sortOrder})</span>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                    {GetText('Views_NoColumnsText')}
                                </div>
                            )}
                        </Space>
                    </Card>
                </Form>
            </Modal>
        </LayoutLeftMenu>
    );
};

export default Views;
